/**
 * Single Source of Truth to determine if an order is Native to Commercium or Synced.
 * Returns `true` for Native, `false` for Synced.
 */
export const isNativeCommerciumOrder = (orderData: any): boolean => {
  if (!orderData) return true; // Fail-safe default
  return orderData.is_created_by_commercium ?? orderData.isCreatedByCommercium ?? true;
};

export const hasOmsOrderAccess = (row: any, omsPlatformId: any) => {
  if (!row) return {hasAccess: false, effectiveOrderId: null}

  const isCreated = isNativeCommerciumOrder(row)

  if (isCreated === true) {
    return {hasAccess: true, effectiveOrderId: row.id}
  }

  if (isCreated === false && omsPlatformId) {
    // Grant access if the root level platform is the OMS
    if (row.platform?.id === omsPlatformId) {
      return {hasAccess: true, effectiveOrderId: row.id}
    }

    const syncedEntry = row.platformorder_set?.find(
      (entry: any) => entry.platform === omsPlatformId && entry.sync_status === 'SYNCED'
    )
    if (syncedEntry) {
      return {hasAccess: true, effectiveOrderId: syncedEntry.id}
    }
  }

  return {hasAccess: false, effectiveOrderId: null}
}

/**
 * Extracts the associated platform ID from an order payload by aggressively checking
 * root, customer, and line item objects.
 */
export const extractOrderPlatformId = (orderData: any): any => {
  if (!orderData) return null;
  return (
    orderData.platform_id ??
    orderData.platform ??
    orderData.customer?.platform_id ??
    orderData.customer?.platform ??
    orderData.line_items?.find((li: any) => li?.platform_product?.platform || li?.platform_product?.platform_id)?.platform_product?.platform ??
    orderData.line_items?.find((li: any) => li?.platform_product?.platform || li?.platform_product?.platform_id)?.platform_product?.platform_id ??
    null
  );
};

/**
 * Validates if the fetched order payload is allowed to be viewed/edited in EditOmsOrder page.
 * Returns whether access is granted and the resolved isCreatedByCommercium flag.
 */
export const validateOmsEditAccess = (orderData: any, activePlatformId: any) => {
  if (!orderData) return { isAccessGranted: false, isCreated: false }
  
  // Actively reject payloads that are actually API errors (like 404 Not Found)
  if (orderData.status === 404 || orderData.detail?.toLowerCase().includes('not found') || orderData.error) {
    return { isAccessGranted: false, isCreated: false }
  }

  const isCreated = isNativeCommerciumOrder(orderData);

  // Check 1: Valid Commercium Created Order
  if (isCreated === true) {
    return { isAccessGranted: true, isCreated }
  }

  // Check 2: Valid Commercium Synced Order
  if (isCreated === false) {
    const resolvedPlatformId = extractOrderPlatformId(orderData);

    if (activePlatformId && resolvedPlatformId === activePlatformId) {
      return { isAccessGranted: true, isCreated }
    }
  }

  return { isAccessGranted: false, isCreated: false }
}
